import { Text } from '@/components/gluestack/text';
import { Skeleton } from '@/components/gluestack/skeleton';
import { Message } from '@/models/Message';
import { ProfilePicture } from '@/components/domain/common/ProfilePicture';
import { MessageTime } from '@/components/ui/MessageTime';
import { RenderHtml } from '@/components/ui/RenderHtml';
import React from 'react';
import { HStack } from '@/components/gluestack/hstack';
import { VStack } from '@/components/gluestack/vstack';
import { View, Pressable } from 'react-native';
import { MessageContextMenu } from './MessageContextMenu';
import type { MessageListType } from '~/constants/lists';
import { useTranslation } from 'react-i18next';
import { parseInboxSentinel } from '@/models/Media';

interface MessageCardProps {
  message?: Message;
  onPress?: (messageId: number) => void;
  isSelected?: boolean;
  onMessageDeleted?: (messageId: number) => void;
  /** Which list the card sits in (the Deleted folder offers Restore instead of Delete). */
  listType?: MessageListType;
}

function MessageCardComponent({
  message,
  onPress,
  isSelected,
  onMessageDeleted,
  listType,
}: MessageCardProps) {
  if (!message) {
    return <MessageCardSkeleton />;
  }

  return (
    <MessageCardContent
      message={message}
      onPress={onPress}
      isSelected={isSelected}
      onMessageDeleted={onMessageDeleted}
      listType={listType}
    />
  );
}

function MessageCardContent({
  message,
  onPress,
  isSelected = false,
  onMessageDeleted,
  listType,
}: MessageCardProps & { message: Message }) {
  const { t } = useTranslation();
  const isUnread = !message.IsRead;
  const messageText = React.useMemo(() => {
    const parsed = parseInboxSentinel(message.LastText);
    if (parsed) {
      const fromMe = message.IsLastFromMe;
      switch (parsed.kind) {
        case 'photo':
          return fromMe ? t('messages.youSentPhoto') : t('messages.photoMessage');
        case 'gif':
          return fromMe ? t('messages.youSentGif') : t('messages.gifMessage');
        case 'sticker':
          return fromMe ? t('messages.youSentSticker') : t('messages.stickerMessage');
        case 'clip':
          return fromMe ? t('messages.youSentClip') : t('messages.clipMessage');
      }
    }
    return message.LastText ? message.LastText : message.Subject || '';
  }, [message.IsLastFromMe, message.LastText, message.Subject, t]);
  const messageClassName = React.useMemo(
    () =>
      `line-clamp-2 text-base leading-tight ${isUnread ? 'font-semibold text-typography-700' : 'text-typography-500'}`,
    [isUnread]
  );

  const handlePress = React.useCallback(() => {
    if (onPress) {
      onPress(message.Id);
    }
  }, [message.Id, onPress]);

  return (
    <Pressable onPress={handlePress}>
      <View
        className={`ml-2 px-4 py-3 ${isSelected ? 'rounded-xl bg-background-0 dark:bg-background-100' : ''}`}>
        <HStack className="items-center gap-3">
          <ProfilePicture member={message.Member} showTooltip />

          <VStack className="flex-1">
            {/* Username and MessageTime */}
            <HStack className="flex-1 items-center gap-1">
              <Text className={`${isUnread ? 'font-bold' : 'font-medium'}`}>
                {message.Member.UserName}
              </Text>
              <Text size="xs" className="mx-0.5 text-typography-500">
                •
              </Text>
              <MessageTime date={message.LastUpdate} className="text-xs text-typography-500" />
            </HStack>

            {/* Message text */}
            <RenderHtml
              html={messageText}
              className={messageClassName}
              characterLimit={500}
              noTruncate
              removeLineBreaks
              size="md"
            />
          </VStack>
          <HStack className="items-center" space="sm">
            {isUnread && <View className="size-2.5 flex-shrink-0 rounded-full bg-secondary-500" />}
            <MessageContextMenu
              message={message}
              onMessageDeleted={onMessageDeleted}
              listType={listType}
            />
          </HStack>
        </HStack>
      </View>
    </Pressable>
  );
}

export const MessageCard = React.memo(MessageCardComponent);

function MessageCardSkeleton() {
  return (
    <View className="ml-2 mr-4 px-4 py-3">
      <HStack className="items-center gap-3">
        <Skeleton className="h-12 w-12 rounded-full" />

        <VStack className="flex-1 gap-2">
          <Skeleton className="h-5 w-36 rounded-md" />

          <VStack className="gap-1.5">
            <Skeleton className="h-5 w-full rounded-md" />
            <Skeleton className="h-5 w-2/3 rounded-md" />
          </VStack>
        </VStack>
      </HStack>
    </View>
  );
}
